import {
  Box,
  CardContent,
  Divider,
  Grid2 as Grid,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CardOutline } from "components/Card";
import { SmallDonutChart } from "components/Charts";
import { FC, useMemo } from "react";

interface StatsPanelProps {
  total: number;
  donutData: {
    id: string;
    label: string;
    value: number;
    color: string;
  }[];
  completionData: {
    total: number;
    completion: number;
    label?: string | null;
    displayText?: string | null; // Custom display text (e.g., "5/10" for fraction)
  };
  avgResponseTime: number;

  totalText?: string;
  donutText?: string;
  completionText?: string;
  avgResponseTimeText?: string;
  onClickBreakdown?: (label: string) => void;
}

export const StatsPanel: FC<StatsPanelProps> = ({
  total,
  donutData,
  completionData,
  avgResponseTime,
  totalText = "Total Feedback",
  donutText = "Status Breakdown",
  completionText = "Completion Rate",
  avgResponseTimeText = "AVG Response Time",
  onClickBreakdown,
}) => {
  const avgResponseTimeFormatted = useMemo(() => {
    const totalMinutes = Math.round(avgResponseTime);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, "0")}Hrs ${minutes.toString().padStart(2, "0")}Min`;
  }, [avgResponseTime]);
  const theme = useTheme();
  const completionDataWithPercentage = useMemo(() => {
    const completionPercentage =
      completionData.total > 0
        ? Math.round((completionData.completion / completionData.total) * 100)
        : 0;
    
    // Clamp completion percentage between 0 and 100
    // If < 0, show 100% completed and 0% remaining
    // If > 100, cap at 100% completed and 0% remaining
    const finalCompletionPercentage = 
      completionPercentage < 0 ? 100 : 
      completionPercentage > 100 ? 100 : 
      completionPercentage;
    const finalRemainingPercentage = 
      completionPercentage < 0 ? 0 : 
      completionPercentage > 100 ? 0 : 
      100 - completionPercentage;
    
    return [
      {
        id: "completed",
        label: "Completed",
        value: finalCompletionPercentage,
        color: theme.palette.primary.main,
        displayText: completionData.displayText || undefined,
      },
      {
        id: "remaining",
        label: "Remaining",
        value: finalRemainingPercentage,
        color: theme.palette.grey[500],
      },
    ];
  }, [completionData, theme]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 12, xl: 4 }}>
          <CardOutline
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent
              sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, xl: 12 }}>
                  <Typography variant="body1" fontWeight={600}>
                    {totalText}
                  </Typography>
                  <Typography variant="h5" component="p">
                    {total}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, xl: 12 }}>
                  <Divider
                    sx={{ my: 2, display: { sm: "none", xl: "block" } }}
                  />
                  <Typography variant="body1" fontWeight={600}>
                    {avgResponseTimeText}
                  </Typography>
                  <Typography variant="h5" component="p">
                    {avgResponseTimeFormatted}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </CardOutline>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, xl: 4 }}>
          <CardOutline>
            <CardContent>
              <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
                {donutText}
              </Typography>
              <Box>
                <SmallDonutChart data={donutData} onClick={onClickBreakdown} />
              </Box>
            </CardContent>
          </CardOutline>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, xl: 4 }}>
          <CardOutline
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent
              sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
            >
              <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
                {completionText}
              </Typography>
              <Box sx={{}}>
                <SmallDonutChart
                  data={completionDataWithPercentage}
                  suffix="%"
                />
              </Box>
            </CardContent>
          </CardOutline>
        </Grid>
      </Grid>
    </Box>
  );
};
